import {
  BaseQueryFn,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from '@reduxjs/toolkit/query/react'
import { alert } from 'actions/flash'
import { replace } from 'react-router-redux'
import { selectIsThanxAdmin } from 'selectors/thanxAdmin'
import { legalDocsRequired } from 'slices/legalDocsBlock'
import { buildBaseUrl } from './utils'

type RbacErrorData = {
  code?: string
  errors?: {
    messages?: string[]
  }
}

type LegalDocsErrorData = {
  errors?: {
    code?: string
  }
}

function buildBaseQuery(baseUrl: string) {
  return fetchBaseQuery({
    baseUrl,
    prepareHeaders: async (headers, { getState }) => {
      const token = (getState() as any).auth.token
      // headers.set('Accept-Version', 'v1.0')
      headers.set('Content-Type', 'application/json')
      headers.set('Accept', process.env.REACT_APP_ACCEPT_HEADER!)

      if (token) {
        headers.set('Authorization', `Bearer ${token}`)
      }

      return headers
    },
  })
}

/**
 * Per-endpoint options an injected endpoint can set via its own
 * `extraOptions` (RTK Query passes an endpoint's `extraOptions` through as
 * this function's third argument on every call) to opt out of a baseQuery-
 * level side effect. `skipErrorToast` is the first of these — added for
 * order_metrics (see orderMetrics.ts), which backs a best-effort estimate on
 * a marketing page and must never surface an error toast to a merchant user
 * who has no action to take on it.
 */
type BaseQueryExtraOptions = {
  skipErrorToast?: boolean
}

export const baseQuery: BaseQueryFn<
  string | FetchArgs,
  unknown,
  FetchBaseQueryError,
  BaseQueryExtraOptions
> = async (args, api, extraOptions) => {
  // The base url is saved to the store when the app launches, so we have
  // to dynamically retrieve it for each request.
  // See https://redux-toolkit.js.org/rtk-query/usage/customizing-queries#constructing-a-dynamic-base-url-using-redux-state
  const state = api.getState() as any
  const result = await buildBaseQuery(buildBaseUrl(api, state))(
    args,
    api,
    extraOptions
  )

  // Show RBAC permission errors as toast notifications.
  // The backend's rbac_authorize! returns { code: 'RBAC_PERMISSION_DENIED' }
  // on 403s, distinguishing them from other 403s (Pundit, business logic).
  // Note this dispatch loop is the ONLY source of an error toast in this
  // function — every other error status (404, 5xx, network failure, timeout)
  // already passes through silently, untouched by baseQuery. `skipErrorToast`
  // guards only the toast itself; the legal-docs block below still applies
  // regardless (a compliance redirect isn't specific to any one endpoint).
  if (result.error && result.error.status === 403) {
    const data = result.error.data as RbacErrorData | undefined
    if (data?.code === 'RBAC_PERMISSION_DENIED') {
      if (!extraOptions?.skipErrorToast) {
        const messages: string[] = data?.errors?.messages || []
        messages.forEach(message => {
          api.dispatch(
            alert({
              key: 'danger',
              message,
              timeout: 10,
            })
          )
        })
      }
    } else {
      const legalData = result.error.data as LegalDocsErrorData | undefined
      // Thanx admins impersonate the merchant's admin user, so the backend
      // can still 403 their requests until nexus exempts thanx_admin?. Skip
      // the Redux + redirect side effects entirely for impersonated sessions
      // so the navbar isn't trapped on /brand_content and `enforced` doesn't
      // get set (which would make `useIsAdmin` flash spurious-true).
      if (
        legalData?.errors?.code === 'legal_docs_required' &&
        !selectIsThanxAdmin(state)
      ) {
        api.dispatch(legalDocsRequired())
        if (!window.location.pathname.startsWith('/brand_content')) {
          api.dispatch(replace('/brand_content#terms_and_policies'))
        }
      }
    }
  }

  return result
}

export const api = createApi({
  reducerPath: 'api',
  tagTypes: [
    'Accounts',
    'AgreementAcceptanceStatus',
    'BrameCampaign',
    'Campaign',
    'CampaignReportSandbox',
    'CaptureRateChannelMappings',
    'CaptureRateRevenueSources',
    'CheckoutSettings',
    'ComplianceStatus',
    'DashboardPromoCards',
    'EmailReplyRouting',
    'ExternalForms',
    'Feedback',
    'FeedbackInbox',
    'FeedbackInboxCounts',
    'FeedbackInboxItem',
    'GiftCardProvider',
    'GrantedRewardImpactPreview',
    'InsightsLab',
    'InsightsLabV2Reports',
    'InsightsLabV2RecommendedReports',
    'InsightsLabV2Favorites',
    'IntegrationsConfig',
    'LandingPageTheme',
    'MenuErrors',
    'LegalDoc',
    'LegalMerchants',
    'Location',
    'MenuCategories',
    'Merchant',
    'MerchantUsers',
    'MergeTag',
    'NavigationTabs',
    'OnboardingSlideshow',
    'OrderingConfig',
    'OrderingErrorConfig',
    'OrderMetrics',
    'OrderRating',
    'PermissionResource',
    'PickerOptions',
    'PointsExpirations',
    'PointsMultiplier',
    'PromoCodesVisibility',
    'Purchase',
    'RevenueCenter',
    'RevenueCenterConfig',
    'Role',
    'SegmentSampleCustomers',
    'ServiceCharge',
    'ServiceChargeGroup',
    'SmartLink',
    'StoredValueMerchantConfig',
    'StoredValueTransaction',
    'StoredValueUser',
    'Suppression',
    'SuppressionNotification',
    'TargetSegment',
    'TermsOfService',
    'User',
    'UserGiftCard',
    'UserList',
    'Promotion',
    'PromotionCode',
    'SegmentActivationPreference',
    'RecoveryConfig',
    'RecoveryStatus',
    'RecoveryLocationConfigs',
    'RecoveryAvailableRewards',
    'RecoveryActivities',
    'RecoveryMetrics',
    'RecoveryConversation',
    'RecoveryEscalation',
    'RecoveryFlagged',
    'RecoveryRestrictedCustomers',
    'RecoveryGamingReview',
    'RecoveryBudget',
    'RecoverySandboxPrompt',
    'RecoveryMakeGoodLadder',
    'RecoveryCannedResponses',
    'RecoveryActivationAdmins',
    'RecoveryActivation',
    'EscalationContacts',
    'LiveUpdatesConfiguration',
    'LiveUpdatesConfigurations',
    'LiveUpdatesLocations',
    'LiveUpdatesSummary',
    'OloWebhookCredential',
    'OrderStatusWebhookReceipts',
    'OrderSatisfaction',
  ],
  baseQuery,
  endpoints: () => ({}),
})
